import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, MessageCircle, Plus, Minus, ArrowRight, Send, Sparkles, Zap } from 'lucide-react';

const faqs = [
  { question: 'У меня нет опыта. Подойдёт ли курс?', answer: 'Да. Курс для новичков с нуля. Начинаем с регистрации — ведём до первых продаж.' },
  { question: 'Сколько денег нужно для старта?', answer: 'От $1,500. Это деньги на товар, не за курс. Вы контролируете объём.' },
  { question: 'За сколько выйти на первые продажи?', answer: 'В среднем 30–60 дней. Зависит от скорости выполнения шагов.' },
  { question: 'EU или USA рынок?', answer: 'Оба. Выбираете под себя — мы помогаем на всех этапах.' },
  { question: 'Сколько времени в неделю?', answer: '5–10 часов. Можно совмещать с работой или бизнесом.' },
  { question: 'Что если Amazon заблокирует аккаунт?', answer: 'В курсе есть блоки по правильной регистрации и работе с поддержкой. Если что — поможем.' },
  { question: 'Будет ли поддержка?', answer: 'Да. Чат, созвоны, разборы, сопровождение до первых продаж.' },
  { question: 'Нужен ли английский?', answer: 'Базовый достаточен. Есть шаблоны для переписки + ИИ-переводчик.' },
  { question: 'Что входит в курс?', answer: '10 модулей (75 уроков) + 9 созвонов. Доступ навсегда.' },
  { question: 'Почему учиться с вами?', answer: '9 созвонов, 75 видео-уроков, довод до продаж. Самые современные и продвинутые методы работы.' },
  { question: 'Как устроены созвоны?', answer: '9 тем: от регистрации до масштабирования. Вживую или в записи.' },
  { question: 'Как устроена программа?', answer: 'Пошагово. Каждый урок — конкретное действие к результату.' },
];

const FAQItem = ({ faq, isOpen, onClick, index, isVisible }) => {
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen]);

  return (
    <div 
      className={`group transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      <div 
        className={`relative overflow-hidden transition-all duration-500 ${
          isOpen 
            ? 'rounded-2xl' 
            : 'rounded-xl'
        }`}
      >
        {/* Animated gradient border */}
        <div className={`absolute inset-0 rounded-2xl transition-opacity duration-500 ${isOpen ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 rounded-2xl animate-gradient bg-[length:200%_auto]" />
          <div className="absolute inset-[2px] bg-zinc-900 rounded-[14px]" />
        </div>
        
        {/* Non-active border */}
        <div className={`absolute inset-0 rounded-xl border-2 transition-all duration-500 ${
          isOpen 
            ? 'border-transparent' 
            : 'border-zinc-800/80 group-hover:border-zinc-700'
        }`} />
        
        {/* Glow effect */}
        {isOpen && (
          <div className="absolute -inset-4 bg-gradient-to-r from-amber-500/20 via-orange-500/30 to-amber-500/20 rounded-3xl blur-2xl -z-10 animate-pulse" />
        )}
        
        {/* Card background */}
        <div className={`relative transition-all duration-500 ${
          isOpen 
            ? 'bg-gradient-to-br from-amber-500/10 via-zinc-900 to-orange-500/10' 
            : 'bg-zinc-900/60 group-hover:bg-zinc-900/80'
        }`}>
          
          {/* Top accent line - animated */}
          <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-orange-400 to-amber-500 bg-[length:200%_auto] animate-gradient transition-all duration-500 ${
            isOpen ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
          }`} />
          
          <button
            onClick={onClick}
            className="w-full px-5 sm:px-6 py-5 flex items-center justify-between gap-4 text-left"
            style={{ touchAction: 'manipulation' }}
          >
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* Number badge with animation */}
              <div className={`relative w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-black text-sm transition-all duration-500 overflow-hidden ${
                isOpen 
                  ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-black scale-110 rotate-3' 
                  : 'bg-zinc-800 text-zinc-500 group-hover:bg-zinc-700 group-hover:text-zinc-400'
              }`}>
                {isOpen && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full animate-[shimmer_2s_infinite]" />
                )}
                <span className="relative z-10">{String(index + 1).padStart(2, '0')}</span>
              </div>
              
              {/* Question with gradient on active */}
              <span className={`font-bold text-base sm:text-lg transition-all duration-500 ${
                isOpen 
                  ? 'bg-gradient-to-r from-white via-amber-100 to-white bg-clip-text text-transparent' 
                  : 'text-zinc-300 group-hover:text-white'
              }`}>
                {faq.question}
              </span>
            </div>
            
            {/* Toggle icon with rotation */}
            <div className={`relative w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 overflow-hidden ${
              isOpen 
                ? 'bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/30' 
                : 'bg-zinc-800 group-hover:bg-zinc-700'
            }`}>
              {isOpen && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full animate-[shimmer_2s_infinite]" />
              )}
              <div className={`transition-transform duration-500 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                {isOpen ? (
                  <Minus className="w-6 h-6 text-black relative z-10" />
                ) : (
                  <Plus className="w-6 h-6 text-zinc-400 group-hover:text-white relative z-10" />
                )}
              </div>
            </div>
          </button>
          
          {/* Answer with smooth height animation */}
          <div 
            className="overflow-hidden transition-all duration-500 ease-out"
            style={{ 
              maxHeight: isOpen ? `${contentHeight}px` : '0px',
              opacity: isOpen ? 1 : 0
            }}
          >
            <div ref={contentRef} className="px-5 sm:px-6 pb-6 pl-[4.5rem] sm:pl-[5rem]">
              <div className="relative">
                {/* Decorative line */}
                <div className="absolute -left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-500 via-orange-500 to-transparent rounded-full" />
                <p className="text-zinc-300 text-base leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  
  const toggleFAQ = (index) => setOpenIndex(openIndex === index ? -1 : index);

  const midPoint = Math.ceil(faqs.length / 2);
  const leftColumn = faqs.slice(0, midPoint);
  const rightColumn = faqs.slice(midPoint);

  return (
    <section 
      ref={sectionRef}
      className="relative py-20 sm:py-28 overflow-hidden bg-[#030304]" 
      id="faq"
    >
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-0 w-[800px] h-[800px] bg-amber-500/[0.03] rounded-full blur-[200px] animate-pulse" />
        <div className="absolute bottom-1/4 left-0 w-[600px] h-[600px] bg-orange-500/[0.03] rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Floating particles */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-amber-500/40 rounded-full animate-pulse"
            style={{
              left: `${10 + (i * 8)}%`,
              top: `${20 + (i * 5) % 60}%`,
              animationDelay: `${i * 0.2}s`,
              animationDuration: `${2 + i * 0.3}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header with animations */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Badge with glow */}
          <div className="relative inline-block mb-6">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/50 to-orange-500/50 rounded-full blur-lg animate-pulse" />
            <div className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 backdrop-blur-xl">
              <HelpCircle className="w-4 h-4 text-amber-400 animate-bounce" style={{ animationDuration: '2s' }} />
              <span className="text-amber-300 font-bold text-sm tracking-wide">ЧАСТЫЕ ВОПРОСЫ</span>
            </div>
          </div>
          
          {/* Animated title */}
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-4">
            <span className="text-white">Ответы на </span>
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                вопросы
              </span>
              {/* Decorative underline */}
              <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none">
                <path d="M2 10C50 2 150 2 198 10" stroke="url(#faq-underline)" strokeWidth="4" strokeLinecap="round" className="animate-[draw_1.5s_ease-out_forwards]" strokeDasharray="200" strokeDashoffset="200" style={{ animation: isVisible ? 'draw 1.5s ease-out forwards 0.5s' : 'none' }}/>
                <defs>
                  <linearGradient id="faq-underline" x1="0" y1="0" x2="200" y2="0">
                    <stop stopColor="#f59e0b"/>
                    <stop offset="0.5" stopColor="#f97316"/>
                    <stop offset="1" stopColor="#f59e0b"/>
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>
          <p className="text-xl text-zinc-400">Коротко и по делу</p>
        </div>

        {/* FAQ Grid with staggered animation */}
        <div className="grid lg:grid-cols-2 gap-4 lg:gap-5 mb-12">
          <div className="space-y-4">
            {leftColumn.map((faq, index) => (
              <FAQItem 
                key={index} 
                faq={faq} 
                index={index}
                isOpen={openIndex === index}
                onClick={() => toggleFAQ(index)}
                isVisible={isVisible}
              />
            ))}
          </div>
          <div className="space-y-4">
            {rightColumn.map((faq, index) => (
              <FAQItem 
                key={index + midPoint} 
                faq={faq} 
                index={index + midPoint}
                isOpen={openIndex === index + midPoint}
                onClick={() => toggleFAQ(index + midPoint)}
                isVisible={isVisible}
              />
            ))}
          </div>
        </div>

        {/* Bottom CTA - MEGA */}
        <div className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative group">
            {/* Animated glow */}
            <div className="absolute -inset-3 bg-gradient-to-r from-amber-500/30 via-orange-500/40 to-amber-500/30 rounded-[2rem] blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500 animate-pulse" />
            
            <div className="relative flex flex-col sm:flex-row items-center justify-center gap-6 p-8 rounded-3xl bg-gradient-to-br from-zinc-900/95 via-zinc-900/90 to-zinc-950/95 border-2 border-amber-500/30 backdrop-blur-xl overflow-hidden">
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              
              <div className="flex items-center gap-4 relative z-10">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl blur-lg opacity-50 animate-pulse" />
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-xl">
                    <MessageCircle className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="text-left">
                  <p className="text-2xl font-black text-white">Ещё вопросы?</p>
                  <p className="text-zinc-400">Ответим в течение 24 часов</p>
                </div>
              </div>
              
              <a 
                href="https://t.me/vadjik"
                target="_blank"
                rel="noopener noreferrer"
                className="group/btn relative flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-black text-lg rounded-2xl shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 overflow-hidden"
                style={{ touchAction: 'manipulation' }}
              >
                {/* Button shine */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
                <Send className="w-5 h-5 relative z-10" />
                <span className="relative z-10">Написать в Telegram</span>
                <ArrowRight className="w-5 h-5 relative z-10 group-hover/btn:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* CSS for draw animation */}
      <style>{`
        @keyframes draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </section>
  );
};

export default FAQSection;
